import { useUserRoleForCurrentWorkspace } from '@/features/roles';
import { authStore } from '@/services/auth/store';
import { getMasqBarVisibility, setMasqBarVisibility } from '@/utils/masq';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Image,
  Paragraph,
  PlainButton,
  SegmentedControl,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useIsEssentialsOnboardingEligible } from '@/features/onboarding';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useBillingLandingHref } from '@/features/billing';
import { useSetupGuideExperiment } from '@/features/experiments';
import { useThemeContext } from '@/context/theme';
import heartPride from '@/assets/images/heart-pride.svg';
import * as styles from './index.css';
import { BRAND_EVERGREEN, BRAND_VERDANT, fireConfetti } from '@/components/feedback/confetti';
import confetti from 'canvas-confetti';
import { HealthLabel } from '@/features/workspace-health';
import { sessionsStore } from '@customerio/hydra-session-state';
import { useAnalytics } from '@/services/analytics';
import { UserRoleSection } from './user-role-section';
import { PersonalSettingsSection } from './personal-settings-section';

const heartShape = confetti.shapeFromPath({
  path: 'M14.7,2.3c-1.6-1.6-4.3-1.6-6,0l-.8.8-.8-.8C5.6.6,2.9.6,1.3,2.3s-1.6,4.3,0,6l.8.8h0s6,6,6,6h0s0,0,0,0l6-6h0s.8-.8.8-.8c1.6-1.6,1.6-4.3,0-6Z',
});

export function PersonDropdown() {
  const router = useRouter();
  const auth = useStore(authStore);
  const ref = useRef(null);
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const billingLandingHref = useBillingLandingHref();
  const { shouldHideSetupGuide } = useSetupGuideExperiment();
  const isEssentialsOnboardingEligible = useIsEssentialsOnboardingEligible(auth.account);
  const setupGuideStatus = useQueryClient().getQueryState(['setupGuide', currentWorkspace?.id])?.status;
  const sessions = useStore(sessionsStore);
  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  /**
   * Shown exactly when `/home` would render the workspace-setup screen, because that screen is
   * where this item leads.
   *
   * It used to require `sessionStorage.showSetUpWorkspaceNavItem`, written by the embedded app's
   * dashboard route on every visit. That route is going away (FPS-2604), and once this app serves
   * the dashboard the user never enters it, so the key stops being written and the item disappears
   * for exactly the accounts still onboarding. Moving the write to a surviving embedded route does
   * not repair it: a user who lands here and never opens an embedded page still never has it
   * written.
   *
   * The conditions below are not a restatement of the key. The key carried the embedded app's
   * `isWorkspaceSetupComplete`, which is a different predicate, not a looser one — it honours the
   * integration skip flag but not the email or messaging ones, and it treats email and messaging
   * as alternatives where the guide requires each step done or skipped. Neither is a subset of the
   * other, so this does move the boundary in both directions.
   *
   * What makes that safe is matching the cohort `selectHomeScreen` sends to `workspace-setup`:
   * a non-builder trial account, outside the essentials-onboarding cohort, whose guide is
   * incomplete. `shouldHideSetupGuide` supplies the trial, sample-workspace and completion halves.
   * That cohort is the only one whose home screen mounts `useReconcileSetupGuide`, so it is the
   * only one where `completed_at` converges after a step is finished outside the setup flow. Show
   * the item to anyone else and it can never clear, because nothing left in either app stamps it.
   *
   * The workspace id is required because the destination is workspace-scoped. Without it
   * `buildLocation` fills the `$workspaceId` segment with `undefined`, and the setup-guide query
   * is disabled in that state, so nothing else in the expression rules it out.
   *
   * The guide's readiness is read straight off the cache rather than from the hook's `isLoading`,
   * which is `isFetching || firstMessageLoading` and so goes true on every background refetch —
   * the item would blink out while home carried on rendering the setup screen from cached data.
   * `status` stays `success` through a refetch, and an outright fetch failure reads as `error`
   * and hides the item instead of presenting a shortcut to a screen that cannot reconcile
   * anything.
   *
   * The entry read is the one `useSetupGuideExperiment` observes, which is what `shouldHideSetupGuide`
   * just above was computed from — the two must describe the same guide or neither means anything.
   * That is the same *kind* of signal `selectHomeScreen` branches on, but not always the same entry:
   * home keys off `getWorkspaceIdFromCurrentUrl()`, a digits-only parse of the path, while this
   * keys off the resolved `useCurrentWorkspace()`. On `/workspaces/last/*` the parse returns null
   * and home's query is disabled outright. Where they diverge the item can appear while home would
   * render its default screen; the href is built from the resolved id, so following it lands on a
   * URL where both agree. Unifying the id source belongs with FPS-4144, not here.
   *
   * `getQueryState` is a synchronous cache read: it registers no observer and no `queryFn`, which
   * matters because `useSetupGuide` and `useSetupGuideExperiment` both claim `['setupGuide', id]`
   * with opposite error handling, so whichever mounts first owns the entry. Observing it from
   * chrome that mounts on every page could move that ownership, and the home page's error
   * behaviour with it. The read is not reactive on its own; this component re-renders on the
   * experiment hook, which observes the same entry. The underlying collision is FPS-4144, and
   * while the swallowing `queryFn` owns the entry an error still reads as `success` — but home
   * reads it the same way, so the two agree.
   */
  const showSetUpWorkspaceNavItem =
    currentWorkspace?.id != null &&
    setupGuideStatus === 'success' &&
    !shouldHideSetupGuide &&
    !isBuilderPlan(auth.account) &&
    !isEssentialsOnboardingEligible;

  const setUpWorkspaceHref = showSetUpWorkspaceNavItem
    ? router.buildLocation({
        to: '/workspaces/$workspaceId/home',
        params: { workspaceId: currentWorkspace?.id },
      }).href
    : null;

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(getMasqBarVisibility);
  const isShowingMasqBarRef = useRef(isShowingMasqBar);
  isShowingMasqBarRef.current = isShowingMasqBar;
  const { track } = useAnalytics();

  const onMasqueradeChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      event.preventDefault();

      const newValue = !isShowingMasqBarRef.current;
      setMasqBarVisibility(newValue);
      setIsShowingMasqBar(newValue);

      // If turning off masq bar, remove masq_account_id from URL
      if (!newValue) {
        const url = new URL(window.location.href);
        url.searchParams.delete('masq_account_id');
        window.location.href = url.toString();
      } else {
        // Reload the page, so that the Journeys UI picks up the local storage update
        window.location.reload();
      }
    },
    [setIsShowingMasqBar],
  );

  const onHeartClick = () => {
    fireConfetti(ref.current, {
      // These hex values match the colors from the heart svg, with the addition of our brand colors
      colors: [
        BRAND_EVERGREEN,
        BRAND_VERDANT,
        '#ea3041',
        '#f77e44',
        '#fbd733',
        '#5fbb5a',
        '#2799dd',
        '#1566bf',
        '#6d28ae',
      ],
      shapes: [heartShape],
    });
  };

  const { colorScheme, setColorScheme } = useThemeContext();

  const onColorSchemeChange = useCallback(
    (value: string) => {
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      const nextColorScheme = value as ColorSchemePreference;

      if (nextColorScheme === colorScheme) return;

      setColorScheme(nextColorScheme);
      void track('darkMode', 'colorSchemeChanged', {
        'Color Scheme': nextColorScheme,
        'Previous Color Scheme': colorScheme,
      });
    },
    [colorScheme, setColorScheme, track],
  );

  return (
    <DropdownMenu initialFocus={-1}>
      {(context) => (
        <IconButton
          label="Menu"
          icon="manage"
          size="md"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
      <DropdownMenuContent>
        <UserRoleSection userRole={userRole} setUpWorkspaceHref={setUpWorkspaceHref} />

        <PersonalSettingsSection />
        <DropdownMenuGroup>
          <DropdownMenuItem href="/settings">Account settings</DropdownMenuItem>
          {auth.account?.partner_account === true && hasPermission('account/edit') === true ? (
            <DropdownMenuItem href="/settings/partner">Partner settings</DropdownMenuItem>
          ) : null}
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/add">Invite teammate</DropdownMenuItem>
          ) : null}
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/invite-guest">Invite guest</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem href="/settings/privacy">Privacy &amp; data</DropdownMenuItem>
          {hasPermission('account/support_access/view') === true ? (
            <DropdownMenuItem href="/settings/ai">AI &amp; MCP</DropdownMenuItem>
          ) : null}
          {hasPermission('account/billing/view') === true ? (
            <DropdownMenuItem href={billingLandingHref}>Plans &amp; billing</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/settings',
                params: { workspaceId: currentWorkspace?.id ?? 'last' },
              }).href
            }
          >
            Workspace settings
          </DropdownMenuItem>
          <DropdownMenuItem
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/health',
                params: { workspaceId: currentWorkspace?.id },
              }).href
            }
          >
            <Box display="flex" alignItems="center" gap="200" justifyContent="space-between">
              <Text>Workspace performance</Text> <HealthLabel />
            </Box>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <SegmentedControl
                flexGrow="1"
                size="sm"
                label="Theme"
                value={colorScheme}
                onChange={onColorSchemeChange}
                options={[
                  { value: 'auto', label: 'System' },
                  { value: 'light', label: 'Light', icon: 'light-mode' },
                  { value: 'dark', label: 'Dark', icon: 'dark-mode' },
                ]}
              />
            </Box>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {showAdminMasqueradeOption ? (
          <DropdownMenuGroup>
            {/* oxlint-disable pluma/enforce-pluma-elements -- menu item acts as the label surface for the toggle. */}
            <DropdownMenuItem
              as="label"
              id="toggle-admin-masq-label"
              htmlFor="toggle-admin-masq"
              shouldCloseOnClick={false}
              // noop onClick to make the whole item styled as interactive
              onClick={() => {}}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Text>Admin masquerade</Text>
                <Toggle
                  size="sm"
                  onChange={(event) => onMasqueradeChange(event)}
                  ariaLabelledby="toggle-admin-masq-label"
                  id="toggle-admin-masq"
                  isChecked={isShowingMasqBar}
                />
              </Box>
            </DropdownMenuItem>
            {/* oxlint-enable pluma/enforce-pluma-elements */}
          </DropdownMenuGroup>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuItem href="/logout" isCritical={true}>
            {sessions.length > 1 ? 'Log out from all accounts' : 'Log out'}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuFooter className={styles.dropdownFooter}>
          <Paragraph sizeVariant="xs" display="flex" flexWrap="wrap" alignItems="center" gap="050">
            <span id="made-with">Made with</span>
            <PlainButton
              aria-labelledby="made-with heart-love around-the-world"
              ref={ref}
              className={styles.heartContainer}
              onClick={onHeartClick}
            >
              <Icon className={styles.heartContainerHeartPlain} name="heart" id="heart-love" aria-label="love" />
              <Image className={styles.heartContainerHeartPride} src={heartPride} aria-label="love" />
            </PlainButton>
            <span id="around-the-world">around the world.</span>
          </Paragraph>
        </DropdownMenuFooter>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
